import { nextTick, ref, watch } from "vue";
import { MEMBER_TYPE } from "./member.types";
import { toValue } from "@erp/shared/utils/to-value";
import { useModal } from "@erp/shared/composables/useModal";
const defaultOptions = {
    setChecker: false,
};
/**
 * @description 멤버 선택 관련 전역으로 사용되는 컴포저블 함수
 * @param memberInfo - 각 스토어에서 생성한 멤버 정보 객체
 * @param setChecker - 확인자 정보 자동 설정 여부
 */
export const useMember = (memberInfo, options = defaultOptions, userData) => {
    const memberInfoRef = toValue(memberInfo);
    const memberType = ref(null);
    const membersList = ref([]);
    const modal = useModal();
    /**
     * @description 선택한 멤버 정보로 해당하는 타입의 멤버를 업데이트하고 모달을 닫습니다.
     * @param item - 선택된 멤버 정보
     */
    const setMember = (item) => {
        memberInfoRef.value.forEach((member) => {
            if (member.type === memberType.value) {
                member.id = item.id ?? null;
                member.givenName = item.givenName ?? "";
                member.userName = item.userName ?? "";
                member.email = item.email ?? "";
            }
        });
    };
    const openMemberDialog = () => {
        const currentMember = memberInfoRef.value.find((m) => m.type === memberType.value);
        modal.openExclusive("MemberSearchDialog", {
            setMember,
            onClose: () => {
                modal.closeAll();
            },
            persistent: true,
            groupIds: currentMember?.groupIds,
        });
    };
    /**
     * @description 전체 멤버 리스트를 저장합니다.
     * @param data - 서버 등에서 전달된 멤버 정보 배열
     */
    const setMemberList = (data) => {
        membersList.value = data;
    };
    /**
     * @description 지정된 타입의 멤버 선택 모달을 엽니다.
     * @param type - 멤버 타입
     */
    const selectMemberDialog = (type) => {
        if (!type)
            return;
        memberType.value = type;
        openMemberDialog();
    };
    /**
     * @description 현재 로그인한 사용자를 확인자로 설정합니다.
     */
    const setSpecificMember = (type, userData) => {
        memberInfoRef.value.forEach((member) => {
            if (member.type === type) {
                member.givenName = userData.givenName ?? "";
                member.id = userData.id ?? null;
                member.userName = userData.userName ?? "";
            }
        });
    };
    /**
     * @description 불러온 확인자, 승인자 정보를 멤버 정보에 적용합니다.
     * @param currentData - 불러온 정보
     */
    const applyMemberInfo = (currentData) => {
        memberInfoRef.value.forEach((member) => {
            if (member.type === MEMBER_TYPE.확인) {
                member.id = currentData.checker ? Number(currentData.checker) : null;
                member.givenName = currentData.checkerName ?? "";
                member.userName = currentData.checkerId ?? "";
            }
            if (member.type === MEMBER_TYPE.승인) {
                member.id = currentData.approver ? Number(currentData.approver) : null;
                member.givenName = currentData.approverName ?? "";
                member.userName = currentData.approverId ?? "";
            }
            if (member.type === MEMBER_TYPE.구매담당자) {
                member.id = currentData.purchaseManager
                    ? Number(currentData.purchaseManager)
                    : null;
                member.givenName = currentData.purchaseManagerName ?? "";
                member.userName = currentData.purchaseManagerId ?? "";
            }
            if (member.type === MEMBER_TYPE.자재담당자) {
                member.id = currentData.materialManager
                    ? Number(currentData.materialManager)
                    : null;
                member.givenName = currentData.materialManagerName ?? "";
                member.userName = currentData.materialManagerId ?? "";
            }
        });
    };
    /**
     * @description 멤버 리스트를 순회하며 선택되지 않은 멤버가 있으면 에러 반환
     */
    const checkMemberInfo = () => {
        const unselected = memberInfoRef.value.filter((member) => !member.id);
        if (unselected.length > 0) {
            // 선택되지 않은 멤버 이름을 모아서 메시지로 표시
            const names = unselected
                .map((member) => member.type)
                .join(", ");
            throw new Error(` ${names}를 선택하여 주십시오`);
        }
    };
    if (options.setChecker) {
        watch(userData?.id, async (newId) => {
            if (newId) {
                await nextTick();
                setSpecificMember(MEMBER_TYPE.확인, {
                    givenName: userData?.givenName.value,
                    id: userData?.id.value,
                    userName: userData?.userName.value,
                });
            }
        }, { immediate: true });
    }
    return {
        memberInfoRef,
        setMember,
        setSpecificMember,
        setMemberList,
        selectMemberDialog,
        checkMemberInfo,
        applyMemberInfo,
        openMemberDialog,
    };
};
